import {
  GetWikiFeedbackResponse,
  InsertWikiFeedbackInput,
  InsertWikiFeedbackResponse,
  UpdateWikiFeedbackResolvedInput,
  UpdateWikiFeedbackResponse,
} from "@/src/models/wiki.models";
import {
  WIKI_FEEDBACK_QUERY_KEY,
  WIKI_MY_FEEDBACK_QUERY_KEY,
} from "@/src/query-keys/wiki.keys";
import gqlClient from "@/src/services/api/graphql.client";
import {
  GET_ALL_WIKI_FEEDBACK_QUERY,
  INSERT_WIKI_FEEDBACK_MUTATION,
  UPDATE_WIKI_FEEDBACK_RESOLVED_MUTATION,
} from "@/src/services/wiki/wiki-feedback.query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";

// Large enough to cover every submission in one page — this is a global admin
// list, not a paginated feed.
const PAGE_SIZE = 1000;

/**
 * All feedback submitted for the wiki as a whole, newest first. Admin-only in
 * the UI, but the hook itself isn't role-gated — callers decide whether to
 * render it.
 */
export const useAllWikiFeedback = () =>
  useQuery({
    queryKey: WIKI_FEEDBACK_QUERY_KEY,
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<GetWikiFeedbackResponse>(
        GET_ALL_WIKI_FEEDBACK_QUERY,
        {
          where: {},
          order: [{ field: "CreatedDate", direction: "DESC" }],
          pageNo: 1,
          pageSize: PAGE_SIZE,
        },
      );
      return data.getwikifeedbacks?.items ?? [];
    },
  });

/**
 * A member's own submissions, newest first — `CreatedBy` is used only to
 * filter here, never rendered (see WikiFeedback model).
 */
export const useMyWikiFeedback = (userId?: string) =>
  useQuery({
    queryKey: [...WIKI_MY_FEEDBACK_QUERY_KEY, userId],
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<GetWikiFeedbackResponse>(
        GET_ALL_WIKI_FEEDBACK_QUERY,
        {
          where: { CreatedBy: { eq: userId } },
          order: [{ field: "CreatedDate", direction: "DESC" }],
          pageNo: 1,
          pageSize: PAGE_SIZE,
        },
      );
      return data.getwikifeedbacks?.items ?? [];
    },
    enabled: !!userId,
  });

export const useSubmitWikiFeedback = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (input: InsertWikiFeedbackInput) => {
      const client = await gqlClient();
      const data = await client.request<InsertWikiFeedbackResponse>(
        INSERT_WIKI_FEEDBACK_MUTATION,
        { input },
      );
      return data.insertwikifeedback;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WIKI_FEEDBACK_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: WIKI_MY_FEEDBACK_QUERY_KEY });
      Toast.show({
        type: "success",
        text1: t("SUCCESS_WIKI_FEEDBACK_SUBMITTED"),
      });
    },
    onError: () => {
      Toast.show({
        type: "error",
        text1: t("ERROR_WIKI_FEEDBACK_SUBMIT_FAILED"),
      });
    },
  });
};

/**
 * Flips a feedback row's resolved state. Super-admin-only surface (see
 * WikiFeedbackList/FeedbackCard); the hook itself isn't role-gated.
 */
export const useUpdateWikiFeedbackResolved = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, resolved }: UpdateWikiFeedbackResolvedInput) => {
      const client = await gqlClient();
      const data = await client.request<UpdateWikiFeedbackResponse>(
        UPDATE_WIKI_FEEDBACK_RESOLVED_MUTATION,
        { itemId, resolved },
      );
      return data.updatewikifeedback;
    },
    onMutate: async ({ itemId, resolved }) => {
      await queryClient.cancelQueries({ queryKey: WIKI_FEEDBACK_QUERY_KEY });
      const previous = queryClient.getQueryData(WIKI_FEEDBACK_QUERY_KEY);

      queryClient.setQueryData(
        WIKI_FEEDBACK_QUERY_KEY,
        (old: { ItemId: string; resolved: boolean | null }[] = []) =>
          old.map((item) =>
            item.ItemId === itemId ? { ...item, resolved } : item,
          ),
      );

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(WIKI_FEEDBACK_QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: WIKI_FEEDBACK_QUERY_KEY });
    },
  });
};
